import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, merge, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { Response } from '@fc-core/services/news.service';

export function loadAllRequests<T>(
  url: string,
  http: HttpClient,
  params?: HttpParams,
  pageSize = 10,
): Observable<{
  list: T[];
  count: number;
}> {
  const requestParams = (params ?? new HttpParams()).set('limit', pageSize);

  return http.get<Response>(url, { params: requestParams }).pipe(
    switchMap((data: Response) => {
      const requests: Observable<Response>[] = [];
      if (data.count > pageSize) {
        for (let offset = pageSize; offset < data.count; offset += pageSize) {
          requests.push(
            http.get<Response>(url, {
              params: requestParams.set('offset', offset),
            }),
          );
        }
        return merge(...requests).pipe(
          startWith(data),
          map((resp: Response) => ({
            list: resp.results,
            count: data.count,
          })),
        );
      }
      return of({
        list: data.results,
        count: data.count,
      });
    }),
  );
}

export function loadAllRequestsInOne<T>(
  url: string,
  http: HttpClient,
  params?: HttpParams,
  pageSize = 10,
): Observable<{
  list: T[];
  count: number;
}> {
  return http.get<Response>(url + `?limit=${pageSize}`, { params }).pipe(
    switchMap((data: Response) => {
      const requests: Observable<Response>[] = [];
      if (data.count > pageSize) {
        const numRequests = Math.ceil(data.count / pageSize);
        for (let i = 1; i <= numRequests; i++) {
          requests.push(
            http.get<Response>(
              url + `?limit=${pageSize}&offset=${i * pageSize}`,
              { params },
            ),
          );
        }
        return forkJoin(requests).pipe(
          map((responses: Response[]) => {
            const list = responses.reduce(
              (acc, cur) => [...acc, ...cur.results],
              data.results,
            );
            return {
              list,
              count: data.count,
            };
          }),
        );
      }
      return of({
        list: data.results,
        count: data.count,
      });
    }),
  );
}
